<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { WidthVariants } from '~/types/Content'
import { getVideoType, getParams } from './mediaHelpers'

export type VideoFields = {
  url: string
  mobileUrl?: string
  posterImage?: {
    fields: {
      file: {
        url: string
      }
    }
  }
  supportingData?: {
    home: {
      showControls?: {
        value: boolean
      }
      structuredData?: {
        value: 'enabled' | 'disabled'
      }
      width?: {
        value: WidthVariants
      }
    }
  }
}

export type VideoProps = {
  fields?: VideoFields
}

const props = withDefaults(defineProps<VideoProps>(), {})

const { t } = useI18n()
const ariaLabel = computed(() => (paused.value ? t('aria_label.video_play') : t('aria_label.video_pause')))

const { mobileUrl, url, posterImage, supportingData } = props.fields ?? {}
const typeDesktop = getVideoType(url)
const typeMobile = getVideoType(mobileUrl)

const params = getParams({ preset: 'grid', breakpoint: 'desktop', quality: '80' })
const posterUrl = posterImage?.fields?.file?.url
const poster = posterUrl ? `${posterUrl}?${params}` : undefined

const showControls = supportingData?.home?.showControls?.value

// Only the first panel's video downloads with the page; the rest wait until they near the viewport.
const isPriority = inject(MEDIA_PRIORITY_KEY, false)

const videoEl = ref<HTMLVideoElement | null>(null)
const paused = ref(showControls || !isPriority)
const play = () => videoEl?.value?.play?.()
const pause = () => videoEl?.value?.pause?.()
const toggle = () => (paused.value ? play() : pause())
const updatePaused = (event: any) => {
  paused.value = event?.target?.paused
}

onMounted(() => {
  if (isPriority) return
  const { stop } = useIntersectionObserver(
    videoEl,
    ([entry]) => {
      if (!entry?.isIntersecting || !videoEl.value) return
      stop()
      videoEl.value.preload = 'auto'
      videoEl.value.autoplay = true
      videoEl.value.play().catch(() => (paused.value = true))
    },
    { rootMargin: '300px' },
  )
})
</script>

<template>
  <div v-if="fields" class="video" data-testid="video">
    <video
      class="video__video"
      data-testid="video__video"
      ref="videoEl"
      @canplay="updatePaused"
      @loadstart="updatePaused"
      @playing="updatePaused"
      @pause="updatePaused"
      :poster="poster"
      :controls="showControls"
      muted
      :autoplay="isPriority"
      :preload="isPriority ? 'auto' : 'none'"
      loop
      playsinline
      defaultMuted
      controlsList="nodownload"
    >
      <source :src="mobileUrl" media="(max-width: 767px)" :type="typeMobile" />
      <source :src="url" :type="typeDesktop" />
    </video>
    <div v-if="!showControls" class="video__control">
      <button type="button" @click.stop.prevent="toggle" :aria-label="ariaLabel">
        <svg-icon :name="paused ? 'mhj:play' : 'mhj:pause'" class="icon" :class="paused ? 'icon-play' : 'icon-pause'" />
      </button>
    </div>
  </div>
</template>

<style lang="postcss">
.video {
  display: grid;
  grid-template-areas: 'video-area';
  overflow: hidden;
  position: relative;

  video,
  .video__control {
    grid-area: video-area;
  }

  .video__control {
    position: relative;
    place-self: start;
    margin: var(--space-30);
    width: min-content;
    height: min-content;
    z-index: 2;

    button {
      display: block;
      position: relative;
      inline-size: var(--space-90);
      block-size: var(--space-90);
      padding: 0;
      line-height: 0;
      user-select: none;

      &:hover {
        transform: scale(1.1);
      }

      &:active {
        transform: scale(1);
      }
    }

    .icon {
      position: absolute;
      inset-block-start: 50%;
      inset-inline-start: 50%;
      inline-size: var(--space-70);
      block-size: var(--space-70);
      pointer-events: none;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
